import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    IconButton,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { colors } from '../../components';
import CustomButton from '../../components/button/ButtonComponent';
import { VerificationBannerContext } from '../../components/layout/dashboard-layout';
import { CustomPagination } from '../../components/pagination/pagination-component';
import CommonTabs from '../../components/tabComponent/commonTabComponent';
import CommonTable from '../../components/tableComponent/commonTableComponent';
import { TextFieldComponent } from '../../components/text-field';
import CommonHeader from '../../components/ui-components/CommonHeader';
import { getUserNotificationsCount } from '../../services/login-service';
import {
    getSupportContractList,
    getSupportFaqs,
    getSupportList,
    getSupportRequestTypes,
    SupportRequestType,
} from '../../services/support-service';
import { RootState } from '../../store';
import { openToast } from '../../store/reducers/toastSlice';
import { setUserNotificationsCount } from '../../store/reducers/userSlice';
import { formatToUSDate } from '../../utils/helper';
import { NavigationConfig } from '../../utils/navigation-constants';
import { useTranslation } from '../../utils/translations';

import { SupportRequestMobileCard } from './components/SupportRequestMobileCard';
import { CreateSupportRequestModal } from './CreateSupportRequestModal';

interface SupportRequest {
    date: string;
    CaseId: string;
    CaseNumber?: string;
    ticketNumber: string;
    requestType: string;
    statusLable?: string;
}

type ApiError = { response?: { data?: { message?: string } }; message?: string };
interface ContractListItem {
    ContractId?: string | number;
    AspireContractId?: string;
}
interface RawFaqItem {
    Question: string;
    Answer: string;
    ImageUrl?: string;
}

const FaqImage: React.FC<{ src: string }> = ({ src }) => {
    const imgRef = useRef<HTMLImageElement>(null);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        const el = imgRef.current;
        if (!el) return;
        const onError = () => setFailed(true);
        el.addEventListener('error', onError);
        return () => el.removeEventListener('error', onError);
    }, []);

    if (failed) return null;
    return (
        <img
            ref={imgRef}
            src={src}
            alt="FAQ attachment"
            style={{
                maxWidth: '300px',
                width: '100%',
                height: 'auto',
                objectFit: 'contain',
                borderRadius: '8px',
                display: 'block',
            }}
        />
    );
};

const FaqAnswerContent: React.FC<{ html: string }> = ({ html }) => {
    const navigate = useNavigate();
    const theme = useTheme();
    const containerRef = useRef<HTMLDivElement>(null);

    const handleAnchorClick = useCallback(
        (e: MouseEvent) => {
            const anchor = (e.target as HTMLElement).closest('a');
            if (!anchor) return;
            e.preventDefault();
            const href = anchor.getAttribute('href');
            if (!href) return;
            try {
                const url = new URL(href, window.location.href);
                const knownAppRoutes = Object.values(NavigationConfig) as string[];
                const isInternalRoute = knownAppRoutes.some(
                    route => url.pathname === route || url.pathname.startsWith(route + '/'),
                );
                if (isInternalRoute) {
                    navigate(url.pathname + url.search + url.hash);
                } else {
                    window.location.href = href;
                }
            } catch {
                navigate(href);
            }
        },
        [navigate],
    );

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        el.addEventListener('click', handleAnchorClick);
        return () => el.removeEventListener('click', handleAnchorClick);
    }, [handleAnchorClick]);

    return (
        <Box
            ref={containerRef}
            sx={{
                ...theme.typography.body1,
                color: 'text.secondary',
                '& p': { margin: 0 },
                '& img': { maxWidth: '100%', height: 'auto' },
                '& a': {
                    color: colors.bluePreset[30],
                    textDecoration: 'underline',
                },
                '& a:visited': {
                    color: colors.bluePreset[30],
                },
                '& a:active': {
                    color: colors.bluePreset[30],
                },
                '& a:hover': {
                    color: colors.bluePreset[10],
                },
            }}
            dangerouslySetInnerHTML={{ __html: html }}
        />
    );
};

export const SupportOverview: React.FC = () => {
    const theme = useTheme();
    const isMobileOrTablet = useMediaQuery(theme.breakpoints.down('md'));
    const { t } = useTranslation();
    const showVerificationBanner = useContext(VerificationBannerContext);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [openModal, setOpenModal] = useState(false);
    const [supportRequests, setSupportRequests] = useState<SupportRequest[]>([]);
    const [contracts, setContracts] = useState<Array<{ id: string; name: string }>>([]);
    const [requestTypes, setRequestTypes] = useState<SupportRequestType[]>([]);
    const [faqData, setFaqData] = useState<Array<{ question: string; answer: string; imageUrl?: string | null }>>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const location = useLocation();
    const supportType = location?.state?.supportType;
    const [totalCount, setTotalCount] = useState(0);
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const activeTabFromState = location?.state?.activeTab; // Get activeTab from navigation state
    const isLoading = useSelector((state: RootState) => state.app.activeHTTPRequests > 0);

    // Derive initial tab from URL query param (?tab=requests or ?tab=faqs)
    const searchParams = new URLSearchParams(location.search);
    const tabFromQuery = searchParams.get('tab');
    const openModalFromQuery = searchParams.get('open') === 'new';
    const tabIndexFromQuery =
        tabFromQuery === 'requests' || tabFromQuery === 'support-requests' ? 1 : tabFromQuery === 'faqs' ? 0 : -1;

    // Active tab state for pagination control
    // Priority: navigation state > URL query param > supportType state > default (0)
    const [activeTab, setActiveTab] = useState<number>(
        activeTabFromState !== undefined
            ? activeTabFromState
            : tabIndexFromQuery >= 0
              ? tabIndexFromQuery
              : supportType === 'addressChange'
                ? 1
                : 0,
    );
    const [expanded, setExpanded] = useState<string | false>('panel0');
    const impersonation = useSelector((state: RootState) => state.impersonation);
    const initialSupportDataFetch = useRef(false);
    // Tracks whether modal should open after data loads (only when requests exist)
    const shouldOpenModalAfterLoad = useRef(openModalFromQuery);

    // Clear location state to reset selectedRequestType
    const clearLocationState = () => {
        navigate(location.pathname, { replace: true, state: {} });
    };

    const showErrorToast = useCallback(
        (message: string) => {
            dispatch(
                openToast({
                    show: true,
                    isAlertOpen: true,
                    message,
                    severity: 'error',
                }),
            );
        },
        [dispatch],
    );

    const handlePageChange = (newPage: number, newRowsPerPage: number) => {
        setPage(newPage + 1);
        setRowsPerPage(newRowsPerPage);
        fetchSupportList(newPage + 1, newRowsPerPage);
    };

    const handleRowsPerPageChange = (newRowsPerPage: number) => {
        setPage(1);
        setRowsPerPage(newRowsPerPage);
        fetchSupportList(1, newRowsPerPage);
    };
    // Handle deep-link params on mount
    useEffect(() => {
        if (supportType === 'addressChange') {
            // Legacy: address change always opens modal
            if (!impersonation?.isImpersonating) {
                setTimeout(() => setOpenModal(true), 500);
            }
        } else if (openModalFromQuery) {
            // ?open=new: switch to requests tab; modal opens after data loads only if records exist
            setActiveTab(1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const tab = params.get('tab');
        if (tab === 'requests' || tab === 'support-requests') {
            setActiveTab(1);
        } else if (tab === 'faqs') {
            setActiveTab(0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    // Fetch support list
    const fetchSupportList = useCallback(
        async (page: number = 1, rowsPerPage: number = 10) => {
            try {
                const response = await getSupportList(true, page, rowsPerPage);
                if (response.statusCode === 200 && Array.isArray(response.data)) {
                    setTotalCount(response.totalCount || response.data.length);

                    const transformedRequests: SupportRequest[] = response.data.map(item => ({
                        date: item.CreatedTime ? formatToUSDate(item.CreatedTime) : 'N/A',
                        CaseId: item.CaseId || 'N/A',
                        ticketNumber: item.CaseNumber || 'N/A',
                        requestType: item.RequestType || 'N/A',
                        subject: item.Subject || 'N/A',
                        status: item.statusLable || 'N/A',
                    }));
                    setSupportRequests(transformedRequests);
                    // Open modal via ?open=new only when there are existing requests
                    if (shouldOpenModalAfterLoad.current && transformedRequests.length > 0) {
                        shouldOpenModalAfterLoad.current = false;
                        if (!impersonation?.isImpersonating) {
                            setOpenModal(true);
                        }
                    }
                } else {
                    showErrorToast(response.message || 'Failed to Load Support Requests');
                }
            } catch (err) {
                console.error('Error fetching support list:', err);
                const e = err as ApiError;
                showErrorToast(e?.response?.data?.message || e?.message || 'Failed to Load Support Requests');
            }
        },
        [impersonation, showErrorToast],
    );

    // Fetch contracts
    const fetchContracts = useCallback(async () => {
        try {
            const response = await getSupportContractList(false);
            if (response.statusCode === 200 && Array.isArray(response.data)) {
                // Transform data to {id, name} format
                const transformedContracts = (response.data as unknown as ContractListItem[]).map(item => ({
                    id: item.ContractId?.toString() || '',
                    name: item.AspireContractId || '',
                }));
                setContracts(transformedContracts);
            } else {
                showErrorToast(response.message || 'Failed to Load Contracts');
            }
        } catch (err) {
            console.error('Error fetching contracts:', err);
            const e = err as ApiError;
            showErrorToast(e?.response?.data?.message || e?.message || 'Failed to Load Contracts');
        }
    }, [showErrorToast]);

    // Fetch request types
    const fetchRequestTypes = useCallback(async () => {
        try {
            const response = await getSupportRequestTypes(false);
            if (response.statusCode === 200 && Array.isArray(response.data)) {
                setRequestTypes(response.data);
            } else {
                showErrorToast(response.message || 'Failed to Load Request Types');
            }
        } catch (err) {
            console.error('Error fetching request types:', err);
            const e = err as ApiError;
            showErrorToast(e?.response?.data?.message || e?.message || 'Failed to Load Request Types');
        }
    }, [showErrorToast]);

    // Fetch FAQs
    const fetchFaqs = useCallback(async () => {
        try {
            const faqsResponse = await getSupportFaqs(true);
            if (faqsResponse.statusCode === 200) {
                const faqsData = faqsResponse.data as unknown as RawFaqItem[];
                const transformedFaqs = faqsData.map(faq => ({
                    question: faq.Question,
                    answer: faq.Answer,
                    imageUrl: faq.ImageUrl || null,
                }));
                setFaqData(transformedFaqs);
            } else {
                showErrorToast(faqsResponse.message || 'Failed to Load FAQs');
            }
        } catch (err) {
            console.error('Error fetching FAQs:', err);
            const e = err as ApiError;
            showErrorToast(e?.response?.data?.message || e?.message || 'Failed to Load FAQs');
        }
    }, [showErrorToast]);

    // Fetch all data on component mount (FAQ and support-list when opening support screen)
    useEffect(() => {
        if (initialSupportDataFetch.current) return;
        initialSupportDataFetch.current = true;

        const fetchAllData = async () => {
            try {
                await Promise.all([
                    fetchContracts(),
                    fetchRequestTypes(),
                    fetchFaqs(),
                    fetchSupportList(1, rowsPerPage),
                ]);
            } catch (err) {
                console.error('Error fetching support data:', err);
            }
        };

        fetchAllData();
    }, [fetchContracts, fetchFaqs, fetchRequestTypes, fetchSupportList, rowsPerPage]);

    // Filter FAQs based on search term
    const filteredFaqData = useMemo(() => {
        if (!searchTerm.trim()) return faqData;
        const lowerSearchTerm = searchTerm.toLowerCase();
        return faqData.filter(
            faq =>
                (faq.question && faq.question.toLowerCase().includes(lowerSearchTerm)) ||
                (faq.answer && faq.answer.toLowerCase().includes(lowerSearchTerm)),
        );
    }, [faqData, searchTerm]);

    const handleChange = (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
        setExpanded(isExpanded ? panel : false);
    };

    const handleSubmitRequest = async () => {
        // Clear location state to reset selectedRequestType
        clearLocationState();
        try {
            // Reset to page 1 when refreshing after submission
            setPage(1);
            setOpenModal(false);
            await fetchSupportList(1, rowsPerPage);
            await fetchUserNotificationsCount();
        } catch (err) {
            console.error('Error refreshing support list:', err);
            showErrorToast((err as ApiError)?.message || 'Failed to refresh support list');
        }
    };

    const fetchUserNotificationsCount = async () => {
        const result = await getUserNotificationsCount();
        const response = result as { statusCode?: number; data?: { unreadCount?: number } };
        if (response.statusCode === 200) {
            dispatch(setUserNotificationsCount(response.data?.unreadCount ?? 0));
        }
    };

    const handleCloseModal = () => {
        // Clear location state to reset selectedRequestType
        clearLocationState();
        setOpenModal(false);
    };

    const handleOpenNewRequest = () => {
        if (impersonation?.isImpersonating) return;
        setOpenModal(true);
    };

    // Shared "New Request" button used in both the empty state and the populated support-requests header
    const renderNewRequestButton = (mdButton = false) => (
        <CustomButton
            startIcon={<span className="icon-plus" />}
            disabled={Boolean(impersonation?.isImpersonating)}
            onClick={handleOpenNewRequest}
            {...(mdButton ? { MDButton: true } : {})}
            sx={{
                width: 'fit-content',
                pointerEvents: impersonation?.isImpersonating ? 'none' : 'auto',
                cursor: impersonation?.isImpersonating ? 'not-allowed' : 'pointer',
                // MDButton spreads theme.typography.caption's fontWeight after this sx, so a
                // top-level `fontWeight` here would get overridden; a nested selector wins instead.
                '&.MuiButtonBase-root': {
                    fontWeight: 700,
                },
            }}
        >
            {t('support.details.newRequest')}
        </CustomButton>
    );

    const handleTabChange = (tabIndex: number) => {
        setActiveTab(tabIndex);
        if (tabIndex === 0) {
            // Keep FAQs on the base support URL (no tab query)
            navigate(location.pathname, { replace: true });
            setExpanded('panel0');
            setSearchTerm(''); // Reset search when switching to FAQs tab
            fetchFaqs();
        } else if (tabIndex === 1) {
            // Keep Support Requests shareable/deep-linkable
            navigate(`${location.pathname}?tab=support-requests`, { replace: true });
            setPage(1);
            fetchSupportList(1, rowsPerPage);
        }
    };

    const tableColumns = [
        { id: 'date', label: t('support.table.date'), sortable: true },
        { id: 'ticketNumber', label: t('support.table.ticketNumber') },
        { id: 'subject', label: t('support.table.subject') },
        { id: 'requestType', label: t('support.table.requestType') },
        { id: 'status', label: t('support.table.status') },
    ];

    // Render FAQ Tab
    const renderFAQTab = () =>
        faqData.length === 0 && !isLoading ? (
            <Box
                sx={{
                    p: 2,
                    borderRadius: 3,
                    bgcolor: colors.fullWhite,
                    m: 3,
                    mt: 0,
                    height: 'calc(100dvh - 255px)',
                }}
            >
                <Stack
                    justifyContent="center"
                    alignItems="center"
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -30%)',
                    }}
                >
                    <Stack justifyContent="center" alignItems="center" gap={1.5}>
                        <Typography variant="h6" color="textSecondary" textAlign={'center'}>
                            {t('support.modal.validation.noRecordsAvailable')}
                        </Typography>
                    </Stack>
                </Stack>
            </Box>
        ) : (
            <Stack px={3}>
                <Box
                    sx={{
                        p: 2,
                        borderRadius: 3,
                        bgcolor: colors.fullWhite,
                        mb: 3,
                        border: `1px solid ${colors.greyPreset[20]}`,
                    }}
                >
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2} flexWrap={'wrap'}>
                        <Typography variant="h6" mb={1.5}>
                            Frequently Asked Questions
                        </Typography>
                        <Box width={{ xs: '100%', sm: 250 }}>
                            <TextFieldComponent
                                placeholder={t('Documents.header.search')}
                                value={searchTerm}
                                className="searchTextFiled"
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                                startIcon={
                                    <span
                                        className="icon-search"
                                        style={{ color: theme.palette.text.secondary, fontSize: 16 }}
                                    />
                                }
                                endIcon={
                                    searchTerm ? (
                                        <IconButton
                                            size="small"
                                            onClick={() => setSearchTerm('')}
                                            aria-label="Clear search"
                                            sx={{ p: 0.5 }}
                                        >
                                            <span
                                                className="icon-close"
                                                style={{ color: theme.palette.text.secondary, fontSize: 18 }}
                                            />
                                        </IconButton>
                                    ) : undefined
                                }
                            />
                        </Box>
                    </Stack>
                    <Stack
                        gap={1.5}
                        sx={{
                            '& .MuiPaper-root': {
                                borderRadius: '12px !important',
                                bgcolor: colors.greyPreset[90],
                                p: 1.5,
                            },
                        }}
                    >
                        {filteredFaqData.length === 0 ? (
                            <Stack
                                justifyContent="center"
                                alignItems="center"
                                sx={{
                                    py: 6,
                                    px: 2,
                                }}
                            >
                                <Stack alignItems="center" gap={1} maxWidth={360}>
                                    <Typography variant="h6" color="textSecondary" textAlign="center">
                                        {t('support.faq.noSearchResults')}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary" textAlign="center">
                                        {t('support.faq.noSearchResultsHint')}
                                    </Typography>
                                    <CustomButton variant="outlined" onClick={() => setSearchTerm('')} sx={{ mt: 1 }}>
                                        {t('support.faq.clearSearch')}
                                    </CustomButton>
                                </Stack>
                            </Stack>
                        ) : (
                            filteredFaqData.map((faq, i) => (
                                <Accordion
                                    key={i}
                                    expanded={expanded === `panel${i}`}
                                    onChange={handleChange(`panel${i}`)}
                                    disableGutters
                                    elevation={0}
                                    sx={{
                                        borderRadius: 3,
                                        '&:before': { display: 'none' },
                                        boxShadow: 'none',
                                        '& .MuiAccordionSummary-root, & .MuiAccordionDetails-root': {
                                            bgcolor: colors.greyPreset[90],
                                            padding: 0,
                                        },
                                        '& .MuiAccordionSummary-root': {
                                            minHeight: 20,
                                        },
                                        '& .MuiAccordionDetails-root': {
                                            paddingTop: theme.spacing(1.5),
                                        },
                                    }}
                                >
                                    <AccordionSummary
                                        expandIcon={
                                            <span
                                                className="icon-down-caret"
                                                style={{ color: colors.fullBlack, fontSize: 20 }}
                                            />
                                        }
                                        sx={{
                                            bgcolor: colors.greyPreset[90],
                                            px: 1.5,
                                            borderRadius: 3,
                                            minHeight: 44,
                                            '& .MuiAccordionSummary-content': {
                                                margin: 0,
                                            },
                                        }}
                                    >
                                        <Typography
                                            variant="subtitle1"
                                            component="h3"
                                            dangerouslySetInnerHTML={{ __html: faq.question }}
                                        />
                                    </AccordionSummary>
                                    {faq.answer && (
                                        <AccordionDetails
                                            sx={{
                                                bgcolor: colors.greyPreset[90],
                                                p: theme.spacing(0, 1.5, 1.5),
                                                borderRadius: '0 0 16px 16px',
                                            }}
                                        >
                                            <FaqAnswerContent html={faq.answer} />
                                            {faq.imageUrl && faq.imageUrl.trim() !== '' && (
                                                <Box sx={{ mt: 1.5, mb: 0.5 }}>
                                                    <FaqImage
                                                        src={
                                                            faq.imageUrl.startsWith('http://') ||
                                                            faq.imageUrl.startsWith('https://')
                                                                ? faq.imageUrl
                                                                : `${process.env.REACT_APP_PROFILE_URL || ''}${faq.imageUrl}`
                                                        }
                                                    />
                                                </Box>
                                            )}
                                        </AccordionDetails>
                                    )}
                                </Accordion>
                            ))
                        )}
                    </Stack>
                </Box>
            </Stack>
        );

    // Render Support Requests Tab
    const renderSupportRequestsTab = () =>
        supportRequests.length === 0 ? (
            <Box
                mx={3}
                p={4.3}
                display="flex"
                justifyContent="center"
                alignItems="center"
                flexDirection="column"
                border={`1px dashed ${colors.greyPreset[30]}`}
                borderRadius={4}
                gap={2}
                height={showVerificationBanner ? 'calc(100dvh - 300px)' : 'calc(100dvh - 264px)'}
            >
                <Typography variant="h5" color="textPrimary">
                    {t('support.details.notCreatedRequest')}
                </Typography>
                <Typography variant="h5" color="textSecondary">
                    {t('support.details.trackManageTicketsSub')}
                </Typography>

                {renderNewRequestButton()}
            </Box>
        ) : isMobileOrTablet ? (
            <Stack sx={{ flex: 1, minHeight: 0, px: 3 }} gap={2}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" color="textPrimary">
                        {t('support.header.yourSupportRequests')}
                    </Typography>
                    {renderNewRequestButton(true)}
                </Stack>
                <Stack gap={1.5}>
                    {supportRequests.map((row, rowIndex) => (
                        <SupportRequestMobileCard
                            key={String(row.CaseId ?? rowIndex)}
                            row={row as unknown as Record<string, unknown>}
                        />
                    ))}
                </Stack>
            </Stack>
        ) : (
            <Stack>
                <Box
                    bgcolor={colors.fullWhite}
                    pt={2}
                    borderRadius={3}
                    mx={3}
                    border={`1px solid ${colors.greyPreset[20]}`}
                >
                    {/* Header with Title and New Request Button */}
                    <Stack direction="row" justifyContent="space-between" alignItems="center" px={2}>
                        <Typography variant="h6" color="textPrimary">
                            {t('support.header.yourSupportRequests')}
                        </Typography>
                        {renderNewRequestButton(true)}
                    </Stack>

                    {/* Table */}
                    <Stack>
                        <CommonTable
                            columns={tableColumns}
                            rows={supportRequests as unknown as Record<string, unknown>[]}
                            statusVariant="support"
                            stickyHeader
                            maxHeight={
                                showVerificationBanner
                                    ? { xs: '100%', lg: 'calc(100dvh - 428px)' }
                                    : { xs: '100%', lg: 'calc(100dvh - 392px)' }
                            }
                            tableBorder={'none'}
                        />
                    </Stack>
                </Box>
            </Stack>
        );

    // Render Pagination
    const renderPagination = () =>
        activeTab === 1 &&
        supportRequests.length > 0 && (
            <CustomPagination
                totalCount={totalCount}
                rowsPerPage={rowsPerPage}
                currentPage={page - 1}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
                serverSide={true}
                sortedRowsLength={supportRequests.length}
            />
        );

    // Main tabs: FAQs and Support Request
    const mainTabItems = [
        {
            label: 'FAQs',
            content: !isLoading && renderFAQTab(),
        },
        {
            label: 'Support Requests',
            content: !isLoading && renderSupportRequestsTab(),
        },
    ];

    return (
        <Stack gap={3}>
            <Box>
                <Box p={3} pb={0}>
                    <CommonHeader
                        mainTitle={t('support.header.mainTitle')}
                        subTitle={t('support.header.subTitle')}
                        icon="icon-help"
                    />
                </Box>
                <Box
                    mt={3}
                    sx={{
                        '& .tabHeader': {
                            mx: 3,
                        },
                    }}
                >
                    <CommonTabs
                        tabs={mainTabItems}
                        defaultActiveIndex={activeTab}
                        activeIndex={activeTab}
                        onTabChange={handleTabChange}
                    />
                </Box>
            </Box>
            <Stack>{renderPagination()}</Stack>

            <CreateSupportRequestModal
                open={openModal}
                onClose={handleCloseModal}
                onSubmit={handleSubmitRequest}
                contracts={contracts}
                requestTypes={requestTypes}
                selectedRequestType={supportType === 'addressChange' ? 2 : ''}
            />
        </Stack>
    );
};
